import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AssignTrainingFormModal, ConfirmationModal, LoadingSpinner, VolunteerUploadDocumentModal } from '../components'
import { VolunteerFormModal } from '../components/VolunteerFormModal'
import { VolunteerProfileCard } from '../components/VolunteerProfileCard'
import { API_BASE_URL, AssignTrainingFormModalMeta, ConfirmationModalMeta, LoadingSpinnerMeta, VolunteerFormModalMeta, VolunteerUploadDocumentFormMeta } from '../data'
import { Training, Volunteer, VolunteerProfileMetaProps } from '../data/types'
import { ChevronLeftIcon } from '../icons'
import { useAuth } from './auth'

type Props = {
    meta: VolunteerProfileMetaProps
    adminSide: boolean
}

export const VolunteerProfile = (props: Props) => {
    const { meta, adminSide } = props
    const { volunteerId } = useParams<{ volunteerId?: string }>()
    const { user, checkAuth } = useAuth()
    const location = useLocation()
    const navigate = useNavigate()

    const [volunteer, setVolunteer] = useState<Volunteer | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [isEditModalOpen, setIsEditModalOpen] = useState(false)
    const [isDeleteTrainingModalOpen, setIsDeleteTrainingModalOpen] = useState(false)
    const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] = useState(false)
    const [isAssigningTrainingModalOpen, setIsAssigningTrainingModalOpen] = useState(false)
    const [isDeletingTraining, setIsDeletingTraining] = useState(false)
    const [selectedTraining, setSelectedTraining] = useState<Training | null>(null)
    const hasAutoOpenedRef = useRef(false)



    useEffect(() => {
        const fetchVolunteerData = async () => {
            if (!volunteer) setLoading(true)
            setError(null)

            try {
                // Determine which volunteer ID to fetch
                const idToFetch = volunteerId || user?.volunteer?.csid

                if (!idToFetch) {
                    throw new Error('No volunteer ID available')
                }

                const response = await fetch(`${API_BASE_URL}/volunteer/${idToFetch}`, {
                    credentials: 'include'
                })
                if (!response.ok) {
                    throw new Error(`Failed to fetch volunteer: ${response.status}`)
                }
                const result = await response.json()
                let data = result.value || result

                // Handle double-nested arrays
                if (Array.isArray(data) && data.length > 0 && Array.isArray(data[0])) {
                    data = data[0]
                }

                setVolunteer(Array.isArray(data) ? data[0] : data)
            } catch (err) {
                setError(err instanceof Error ? err.message : 'An error occurred while fetching volunteer data')
            } finally {
                setLoading(false)
            }
        }

        if (volunteerId || user?.volunteer?.csid) {
            fetchVolunteerData()
        }
    }, [volunteerId, user])

    // Auto-open edit modal if navigated from home page warning
    useEffect(() => {
        if (location.state?.openEditModal && !loading && volunteer && !hasAutoOpenedRef.current) {
            hasAutoOpenedRef.current = true
            setIsEditModalOpen(true)
            // Clear the location state to prevent re-opening after form submission
            navigate(location.pathname, { replace: true, state: {} })
        }
    }, [location.state, loading, volunteer, navigate, location.pathname])

    const handleEditVolunteer = async (volunteerData: any) => {
        if (!volunteer?.csid) return


        try {
            // Remove fields that shouldn't be updated
            const { id, csid, created_at, updated_at, age, ...updateData } = volunteerData



            const response = await fetch(`${API_BASE_URL}/volunteer/${volunteer.csid}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(updateData)
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                console.error('Validation errors:', errorData)
                throw new Error(errorData.message || `Failed to update volunteer: ${response.status}`)
            }

            const result = await response.json()
            let updatedVolunteer = result.model || result.value || result

            // Handle double-nested arrays
            if (Array.isArray(updatedVolunteer) && updatedVolunteer.length > 0 && Array.isArray(updatedVolunteer[0])) {
                updatedVolunteer = updatedVolunteer[0]
            }

            setVolunteer(Array.isArray(updatedVolunteer) ? updatedVolunteer[0] : updatedVolunteer)

            // Refresh the global user state to update profile_submitted_at
            await checkAuth()
        } catch (err) {
            console.error('Error updating volunteer:', err)
            throw err
        }
    }


    const handleSubmitTrainings = async (assignedTrainings: Training[]) => {
        if (!volunteer) return

        const updatedTrainingIds = assignedTrainings.map(t => t.id)

        const response = await fetch(`${API_BASE_URL}/training/volunteer/${volunteer.csid}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({
                training: updatedTrainingIds
            })
        })

        if (!response.ok) {
            throw new Error(response.statusText)
        }

        const refreshed_volunteer = await fetch(`${API_BASE_URL}/volunteer/${volunteer.csid}`, {
            credentials: "include"
        })

        const result = await refreshed_volunteer.json()
        const updated = result.value || result

        setVolunteer(updated)
    }


    const handleAssignTrainingCancel = () => {
        setIsAssigningTrainingModalOpen(false)
    }

    const handleDeleteTrainingConfirm = async () => {

        try {
            setIsDeletingTraining(true)
            if (!selectedTraining) {
                throw new Error("No training has been selected")
            }

            const response = await fetch(`${API_BASE_URL}/training/${selectedTraining.id}/volunteer/${volunteer.csid}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json'
                },
                credentials: 'include'
            })

            if (!response.ok) {
                setSelectedTraining(null)
                throw new Error("Failed to unassign training.")
            }

            const refreshed_volunteer = await fetch(`${API_BASE_URL}/volunteer/${volunteer.csid}`, {
                credentials: "include"
            })

            const result = await refreshed_volunteer.json()
            const updated = result.value || result

            setVolunteer(updated)

        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to unassign training.")
        }
        finally {
            setIsDeletingTraining(false)
            setIsDeleteTrainingModalOpen(false)
        }
    }

    const handleSelectTrainingDelete = (training) => {
        setSelectedTraining(training)
        setIsDeleteTrainingModalOpen(true)
    }

    const handleDeleteTrainingCancel = () => {
        setIsDeleteTrainingModalOpen(false)
        setSelectedTraining(null)
    }


    const handleDocumentUpload = async (data: { documentType: string, file: File }) => {
        const { documentType, file } = data
        const filename = file.name;

        const response = await fetch(`${API_BASE_URL}/file`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ filename, folder: 'volunteer_documents' })
        })

        if (!response.ok) {
            const errorData = await response.json().catch(() => null)
            console.error('File upload error:', { status: response.status, errorData })
            const errorMessage = errorData?.message || errorData?.error || 'Failed to get upload URL'
            throw new Error(errorMessage)
        }

        const result = await response.json()
        const { url: presignedUrl, key } = result.data || result

        // Upload file to storage using presigned URL
        const uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
                'Accept': 'application/json'
            }
        })

        if (!uploadResponse.ok) {
            throw new Error('Failed to upload file')
        }

        // call put function to update uploaded documents field

        const updatedDocuments = [
            ...(volunteer.uploaded_documents || []),
            {
                type: documentType,
                name: file.name,
                file_key: key,
                uploaded_at: Date.now().toString()
            }
        ];

        const volunteerData = {
            csid: volunteer.csid,
            first_name: volunteer.first_name,
            last_name: volunteer.last_name,
            second_last_name: volunteer.second_last_name,
            gender: volunteer.gender,
            dob: volunteer.dob,
            phone: volunteer.phone,
            country_code: volunteer.country_code,
            email: volunteer.email,
            country: volunteer.country,
            country_of_birth: volunteer.country_of_birth,
            jamat_khana: volunteer.jamat_khana,
            family_origin: volunteer.family_origin,
            service_location: volunteer.service_location,
            service_record: volunteer.service_record,
            languages: volunteer.languages,
            support_needs: volunteer.support_needs,
            ismaili: volunteer.ismaili,
            multi_faith_family: volunteer.multi_faith_family,
            registered_volunteer: volunteer.registered_volunteer,
            eligible_for_duty: volunteer.eligible_for_duty,
            volunteer_for_visit: volunteer.volunteer_for_visit,
            uk_didar_registration_ack: volunteer.uk_didar_registration_ack,
            international_volunteer_registration_ack: volunteer.international_volunteer_registration_ack,
            uploaded_documents: updatedDocuments
        };

        await handleEditVolunteer(volunteerData);
    }



    return (
        <Fragment>
            <div className="flex-1 bg-gray-50 dark:bg-gray-900 pt-8">
                <div className="container mx-auto px-4 pb-12">
                    {adminSide && (<Link
                        to="/volunteer"
                        className="mb-4 inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                        <ChevronLeftIcon className="w-5 h-5 me-2" />
                        {meta.backToVolunteersButton}
                    </Link>)}
                    {loading && (
                        <div className="flex justify-center items-center min-h-[400px]">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    )}

                    {!loading && error && (
                        <div className="max-w-2xl mx-auto">
                            <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p className="font-bold text-lg mb-2">{meta.errorTitle}</p>
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {!loading && !error && volunteer && (
                        <>
                            <VolunteerProfileCard
                                volunteer={volunteer}
                                meta={meta}
                                onEdit={() => setIsEditModalOpen(true)}
                                onDelete={handleSelectTrainingDelete}
                                onAssign={() => setIsAssigningTrainingModalOpen(true)}
                                onUploadDocuments={() => setIsUploadDocumentModalOpen(true)}
                                adminSide={adminSide}
                            />
                            <VolunteerUploadDocumentModal
                                meta={VolunteerUploadDocumentFormMeta}
                                isOpen={isUploadDocumentModalOpen}
                                onClose={() => setIsUploadDocumentModalOpen(false)}
                                onSubmit={handleDocumentUpload}
                                volunteer={volunteer}
                            />
                        </>
                    )}
                </div>
            </div>

            {volunteer && (
                <VolunteerFormModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSubmit={handleEditVolunteer}
                    initialVolunteer={volunteer}
                    meta={VolunteerFormModalMeta}
                    adminSide={adminSide}
                />
            )}

            {volunteer && (
                <AssignTrainingFormModal
                    meta={AssignTrainingFormModalMeta}
                    volunteer_trainings={volunteer.trainings || []}
                    isOpen={isAssigningTrainingModalOpen}
                    onClose={handleAssignTrainingCancel}
                    onSubmit={handleSubmitTrainings}
                />
            )}

            <ConfirmationModal
                meta={{
                    ...ConfirmationModalMeta,
                    confirmButton: meta.deleteTrainingTitle,
                    deletingButton: 'Unassigning...'
                }}
                isOpen={isDeleteTrainingModalOpen}
                onConfirm={handleDeleteTrainingConfirm}
                onCancel={handleDeleteTrainingCancel}
                title={meta.deleteTrainingTitle}
                message={meta.deleteTrainingMessage}
                isLoading={isDeletingTraining}
            />

        </Fragment>
    )
}
